import { useCallback, useEffect, useState } from "react";
import "./ChangeBillingCycleModal.scss";
import { Modal } from "antd";
import { Button } from "../misc/Button";
import { ProStar } from "../misc/ProStar";
import AccountsApi from "../../../api/AccountsApi";
import { handleServerError } from "../../../util/ErrorHandler";
import { useRecoilState } from "recoil";
import { memberState } from "../../../state/atoms";
import {
  LightCardDto,
  SubStatusDto,
  SubStatusDtoPlanCycleEnum,
} from "../../../generated/account-client";
import { Spinner } from "../misc/Spinner";
import DateTimeStamp from "../misc/DateTimeStamp";
import { success } from "../../../service/MessageService";
import CardOnFile from "../misc/CardOnFile";

interface ChangeBillingCycleModalProps {
  cycle?: SubStatusDtoPlanCycleEnum;
  loading: boolean;
  visible: boolean;
  onClose: (refresh?: boolean) => void;
}

export default function ChangeBillingCycleModal({
  cycle,
  loading,
  visible,
  onClose,
}: ChangeBillingCycleModalProps) {
  const [member] = useRecoilState(memberState);
  const [card, setCard] = useState({} as LightCardDto);
  const [newCycle, setNewCycle] = useState(cycle);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [canUpgrade, setCanUpgrade] = useState(false);
  const [monthlySubscription, setMonthlySubscription] = useState(
    {} as SubStatusDto
  );
  const [annualSubscription, setAnnualSubscription] = useState(
    {} as SubStatusDto
  );
  const [selectedSubscription, setSelectedSubscription] = useState(
    {} as SubStatusDto
  );

  const fetchPaymentDetails = useCallback(() => {
    if (!member.id) return;

    if (visible) {
      AccountsApi.getPaymentDetails(member.id)
        .then((resp) => {
          if (resp.data.card) {
            setCard(resp.data.card);
          }
        })
        .catch((e) => {
          handleServerError(e);
        });
    }
  }, [member.id, visible]);

  useEffect(fetchPaymentDetails, [fetchPaymentDetails]);

  useEffect(() => {
    setNewCycle(cycle);
  }, [cycle]);

  useEffect(() => {
    setCanUpgrade(newCycle !== cycle);
  }, [newCycle, cycle]);

  useEffect(() => {
    setSelectedSubscription(
      newCycle === SubStatusDtoPlanCycleEnum.Monthly
        ? monthlySubscription
        : annualSubscription
    );
  }, [newCycle, annualSubscription, monthlySubscription]);

  useEffect(() => {
    if (!member.id) return;

    if (visible) {
      setLoadingPreview(true);
      AccountsApi.getAvailableSubscriptions(member.id)
        .then((resp) => {
          setLoadingPreview(false);
          const as = resp.data.find(
            (it) => it.planCycle === SubStatusDtoPlanCycleEnum.Yearly
          );
          const ms = resp.data.find(
            (it) => it.planCycle === SubStatusDtoPlanCycleEnum.Monthly
          );
          if (as) {
            setAnnualSubscription(as);
          }
          if (ms) {
            setMonthlySubscription(ms);
          }
        })
        .catch((e) => {
          handleServerError(e);
          setLoadingPreview(false);
          onClose();
        });
    }
  }, [visible, member.id, onClose]);

  function updateSubscription() {
    setLoadingPreview(true);
    AccountsApi.changeSubscriptionPlan(
      member.id,
      selectedSubscription.planCycle!,
      false
    )
      .then((resp) => {
        setLoadingPreview(false);
        success("Plan successfully changed!");
        onClose(true);
      })
      .catch((e) => {
        handleServerError(e);
        setLoadingPreview(false);
      });
  }

  return (
    <Modal
      className={"manage-billing-cycle-modal"}
      visible={visible}
      onCancel={() => onClose()}
    >
      <h3>
        Manage your Pro
        <ProStar /> subscription
      </h3>

      <CardOnFile card={card} />

      <h5>Change Billing Cycle</h5>
      {loadingPreview && <Spinner />}

      {!loadingPreview && (
        <>
          <section className={"billing-cycle"}>
            <div
              className={`${
                newCycle === SubStatusDtoPlanCycleEnum.Yearly
                  ? "billing-cycle-container selected"
                  : "billing-cycle-container"
              }`}
              onClick={() => setNewCycle(SubStatusDtoPlanCycleEnum.Yearly)}
            >
              <h5>Pro Plan Annual</h5>
              <span>
                <b>${annualSubscription.planTotal! / 12}</b>/month, paid
                annually
              </span>
            </div>

            <div
              className={`${
                newCycle === SubStatusDtoPlanCycleEnum.Monthly
                  ? "billing-cycle-container selected"
                  : "billing-cycle-container"
              }`}
              onClick={() => setNewCycle(SubStatusDtoPlanCycleEnum.Monthly)}
            >
              <h5>Pro Plan Monthly</h5>
              <span>
                <b>${monthlySubscription.planTotal}</b>/month, paid monthly
              </span>
            </div>
          </section>

          <ul className={"billing-cycle"}>
            {!!selectedSubscription.dueToday && canUpgrade && (
              <li>
                <span>Due today (pro-rated)</span>
                <span>
                  <b>${selectedSubscription.dueToday}</b>
                </span>
              </li>
            )}

            <li>
              <span>Recurring charge</span>
              <span>
                <b>${selectedSubscription.planTotal}</b>
              </span>
            </li>
            <li>
              <span>Next billing date</span>
              <span>
                <b>
                  <DateTimeStamp
                    date={selectedSubscription.nextPlanBillingDate!}
                    dateOnly
                  />
                  ,{" "}
                  {newCycle === SubStatusDtoPlanCycleEnum.Monthly
                    ? "1 month"
                    : "1 year"}
                </b>
              </span>
            </li>
          </ul>

          {canUpgrade && (
            <footer>
              <Button
                type={"Primary"}
                onClick={updateSubscription}
                disabled={loading}
              >
                <span>Update Subscription</span>
              </Button>
            </footer>
          )}
        </>
      )}
    </Modal>
  );
}
